import {
  ChevronDown,
  Loader2,
  Plug,
  PlugZap,
  RefreshCw,
  Trash2,
  Wrench,
} from "lucide-react"
import * as React from "react"
import { toast } from "sonner"

import { PageHeader } from "@/components/layout"
import { EmptyState } from "@/components/empty-state"
import { ConnectionStatusBadge } from "@/components/status-badge"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { useLocale, useT } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import type { AuthType, Connection, McpTool } from "@/lib/types"
import { formatRelative } from "@/lib/utils"

export function ConnectionsPage() {
  const t = useT()
  const [connections, setConnections] = React.useState<Connection[]>([])
  const [loading, setLoading] = React.useState(true)
  const [dialogOpen, setDialogOpen] = React.useState(false)

  const load = React.useCallback(async () => {
    try {
      setConnections(await api.listConnections())
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("connections.toasts.loadFailed"))
    } finally {
      setLoading(false)
    }
  }, [t])

  React.useEffect(() => {
    void load()
  }, [load])

  return (
    <>
      <PageHeader
        title={t("connections.title")}
        description={t("connections.description")}
        action={<Button onClick={() => setDialogOpen(true)}>{t("connections.addServer")}</Button>}
      />

      <div className="space-y-4 p-6">
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-28 w-full" />
          </div>
        ) : connections.length === 0 ? (
          <EmptyState
            icon={Plug}
            title={t("connections.empty.title")}
            description={t("connections.empty.description")}
            actionLabel={t("connections.empty.action")}
            onAction={() => setDialogOpen(true)}
          />
        ) : (
          connections.map((connection) => (
            <ConnectionCard key={connection.id} connection={connection} onChange={load} />
          ))
        )}
      </div>

      <AddConnectionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={load}
      />
    </>
  )
}

function ConnectionCard({
  connection,
  onChange,
}: {
  connection: Connection
  onChange: () => Promise<void>
}) {
  const t = useT()
  const { locale } = useLocale()
  const [tools, setTools] = React.useState<McpTool[] | null>(null)
  const [busy, setBusy] = React.useState<string | null>(null)

  async function withBusy(label: string, work: () => Promise<void>) {
    setBusy(label)
    try {
      await work()
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("common.actionFailed"))
    } finally {
      setBusy(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>{connection.name}</CardTitle>
          <ConnectionStatusBadge status={connection.status} enabled={connection.enabled} />
          <Badge variant="outline">{connection.transport}</Badge>
          {connection.auth.has_secret && (
            <Badge variant="secondary">
              {connection.auth.type} {connection.auth.hint}
            </Badge>
          )}
        </div>
        <CardDescription className="break-all">{connection.url}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {connection.last_error && (
          <Alert variant="destructive">
            <AlertTitle>{t("connections.lastAttemptFailedTitle")}</AlertTitle>
            <AlertDescription>{connection.last_error}</AlertDescription>
          </Alert>
        )}

        <div className="text-muted-foreground flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <span>
            <Wrench className="mr-1 inline size-3.5" />
            {t("common.toolCount", { count: connection.tool_count })}
          </span>
          {connection.server_name && (
            <span>
              {t("connections.serverInfo", {
                name: connection.server_name,
                version: connection.server_version ? ` ${connection.server_version}` : "",
              })}
            </span>
          )}
          <span>{t("connections.checkedAt", { when: formatRelative(connection.last_checked_at, locale) })}</span>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={busy !== null}
            onClick={() =>
              withBusy("test", async () => {
                await api.testConnection(connection.id)
                toast.success(t("connections.toasts.reachable", { name: connection.name }))
                await onChange()
              })
            }
          >
            {busy === "test" ? <Loader2 className="size-4 animate-spin" /> : <PlugZap className="size-4" />}
            {t("connections.test")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={busy !== null}
            onClick={() =>
              withBusy("refresh", async () => {
                const result = await api.refreshConnection(connection.id)
                toast.success(
                  t("connections.toasts.catalogUpdated", {
                    added: result.added,
                    updated: result.updated,
                    removed: result.removed,
                  }),
                )
                setTools(result.tools)
                await onChange()
              })
            }
          >
            {busy === "refresh" ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <RefreshCw className="size-4" />
            )}
            {t("connections.refreshTools")}
          </Button>

          <Collapsible>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  if (tools === null) {
                    try {
                      setTools(await api.connectionTools(connection.id))
                    } catch {
                      toast.error(t("connections.toasts.loadToolsFailed"))
                    }
                  }
                }}
              >
                <ChevronDown className="size-4" />
                {t("connections.browseTools")}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <ToolList tools={tools} />
            </CollapsibleContent>
          </Collapsible>

          <Button
            variant="ghost"
            size="sm"
            className="text-destructive ml-auto"
            disabled={busy !== null}
            onClick={() =>
              withBusy("delete", async () => {
                if (!confirm(t("connections.removeConfirm", { name: connection.name }))) return
                await api.deleteConnection(connection.id)
                toast.success(t("connections.toasts.removed", { name: connection.name }))
                await onChange()
              })
            }
          >
            <Trash2 className="size-4" />
            {t("connections.remove")}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function ToolList({ tools }: { tools: McpTool[] | null }) {
  const t = useT()
  if (tools === null) return <Skeleton className="h-20 w-full" />
  if (tools.length === 0) {
    return <p className="text-muted-foreground text-sm">{t("connections.noTools")}</p>
  }
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {tools.map((tool) => (
        <div key={tool.id} className="rounded-lg border p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="payload font-medium">{tool.name}</span>
            {tool.unsupported_reason && <Badge variant="warning">{t("connections.unsupported")}</Badge>}
          </div>
          {tool.description && (
            <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">{tool.description}</p>
          )}
          <p className="text-muted-foreground payload mt-2 opacity-70">
            {t("connections.callsAs", { name: tool.llm_name })}
          </p>
        </div>
      ))}
    </div>
  )
}

function AddConnectionDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: () => Promise<void>
}) {
  const t = useT()
  const [name, setName] = React.useState("")
  const [url, setUrl] = React.useState("")
  const [authType, setAuthType] = React.useState<AuthType>("none")
  const [headerName, setHeaderName] = React.useState("")
  const [secret, setSecret] = React.useState("")
  const [busy, setBusy] = React.useState(false)

  React.useEffect(() => {
    if (!open) {
      setName("")
      setUrl("")
      setAuthType("none")
      setHeaderName("")
      setSecret("")
    }
  }, [open])

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    try {
      const result = await api.createConnection({
        name,
        url,
        auth: {
          type: authType,
          header_name: authType === "header" ? headerName : null,
          secret: authType === "none" ? null : secret,
        },
      })
      toast.success(t("connections.connectedCount", { count: result.tools.length }))
      onOpenChange(false)
      await onCreated()
    } catch (error) {
      toast.error(
        error instanceof ApiError ? error.message : t("connections.toasts.connectFailed"),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("connections.dialog.title")}</DialogTitle>
          <DialogDescription>{t("connections.dialog.description")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="conn-name">{t("common.name")}</Label>
            <Input
              id="conn-name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("connections.dialog.namePlaceholder")}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="conn-url">{t("connections.dialog.urlLabel")}</Label>
            <Input
              id="conn-url"
              required
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://tools.example.com/mcp"
            />
            <p className="text-muted-foreground text-xs">{t("connections.dialog.urlHint")}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="conn-auth">{t("connections.dialog.authLabel")}</Label>
            <Select value={authType} onValueChange={(value) => setAuthType(value as AuthType)}>
              <SelectTrigger id="conn-auth">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("connections.dialog.authNone")}</SelectItem>
                <SelectItem value="bearer">{t("connections.dialog.authBearer")}</SelectItem>
                <SelectItem value="header">{t("connections.dialog.authHeader")}</SelectItem>
                <SelectItem value="basic">{t("connections.dialog.authBasic")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {authType === "header" && (
            <div className="space-y-2">
              <Label htmlFor="conn-header">{t("connections.dialog.headerNameLabel")}</Label>
              <Input
                id="conn-header"
                required
                value={headerName}
                onChange={(event) => setHeaderName(event.target.value)}
                placeholder="X-API-Key"
              />
            </div>
          )}

          {authType !== "none" && (
            <div className="space-y-2">
              <Label htmlFor="conn-secret">
                {authType === "basic"
                  ? t("connections.dialog.secretLabelBasic")
                  : t("connections.dialog.secretLabel")}
              </Label>
              <Input
                id="conn-secret"
                type="password"
                required
                value={secret}
                onChange={(event) => setSecret(event.target.value)}
              />
              <p className="text-muted-foreground text-xs">{t("connections.dialog.secretHint")}</p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : null}
              {busy ? t("connections.dialog.connecting") : t("connections.dialog.connect")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
